import { useGetAllProductsQuery, useAddOrderQuoteItemsMutation } from '@codezo/graphql';
import { Box, Loader2, Plus, Search, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../../../components/ui/dialog';
import { DatePicker } from '../../../../components/ui/date-picker';
import { Label } from '../../../../components/ui/label';
import { NumberInput } from '../../../../components/ui/number-input';
import { ProductImage } from '../../../../components/ui/product-image';
import { cn } from '../../../../lib/utils';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';

interface AddProductsToQuoteModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	/** Product IDs already in the quote, to show as "already added" */
	existingProductIds?: string[];
	onSuccess: () => void;
}

interface SelectedProduct {
	id: string;
	name: string;
	sku: string;
	imageUrl?: string;
	quantities: number[];
	deadline: string | null;
}

export function AddProductsToQuoteModal({
	open,
	onOpenChange,
	quoteId,
	existingProductIds = [],
	onSuccess,
}: AddProductsToQuoteModalProps) {
	const [step, setStep] = useState<'products' | 'quantities'>('products');
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<Map<string, SelectedProduct>>(new Map());

	const [addItems, { loading: adding }] = useAddOrderQuoteItemsMutation();

	const { data, loading } = useGetAllProductsQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: !open || step !== 'products',
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllProduct?.data,
		pageInfo: data?.getAllProduct?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	const reset = () => {
		setStep('products');
		setQuery('');
		setSelected(new Map());
		scroll.reset();
		setPage(1);
	};

	const handleOpenChange = (next: boolean) => {
		if (!next) reset();
		onOpenChange(next);
	};

	const toggleProduct = (
		product: Pick<NonNullable<(typeof scroll.items)[0]>, 'id' | 'name' | 'sku' | 'images'>
	) => {
		// A product already on the quote cannot be added again — `addOrderQuoteItems`
		// refuses the whole batch over it. Its tiers are changed from its row on the
		// quote, which can identify them and say what an edit costs.
		if (existingProductIds.includes(product.id)) return;
		setSelected((prev) => {
			const next = new Map(prev);
			if (next.has(product.id)) {
				next.delete(product.id);
			} else {
				const primary =
					product.images?.find((i) => i.isPrimary)?.url ??
					product.images?.[0]?.url ??
					undefined;
				next.set(product.id, {
					id: product.id,
					name: product.name,
					sku: product.sku,
					imageUrl: primary,
					quantities: [0],
					deadline: null,
				});
			}
			return next;
		});
	};

	const handleNext = () => {
		if (selected.size === 0) return;
		// Nothing selectable is already on the quote, so every product here is new
		// and starts with an empty tier list.
		setStep('quantities');
	};

	const updateQuantity = (productId: string, index: number, value: number) => {
		setSelected((prev) => {
			const next = new Map(prev);
			const entry = next.get(productId);
			if (!entry) return prev;
			const quantities = [...entry.quantities];
			quantities[index] = value;
			next.set(productId, { ...entry, quantities });
			return next;
		});
	};

	const addQuantity = (productId: string) => {
		setSelected((prev) => {
			const next = new Map(prev);
			const entry = next.get(productId);
			if (!entry || entry.quantities.length >= 3) return prev;
			next.set(productId, { ...entry, quantities: [...entry.quantities, 0] });
			return next;
		});
	};

	const removeQuantity = (productId: string, index: number) => {
		setSelected((prev) => {
			const next = new Map(prev);
			const entry = next.get(productId);
			if (!entry || entry.quantities.length <= 1) return prev;
			next.set(productId, {
				...entry,
				quantities: entry.quantities.filter((_, i) => i !== index),
			});
			return next;
		});
	};

	const handleAdd = async () => {
		try {
			const items = Array.from(selected.values()).map((p) => ({
				productId: p.id,
				quantities: p.quantities.filter((q) => q > 0),
				deadline: p.deadline ?? undefined,
			}));

			if (items.every((i) => i.quantities.length === 0)) {
				toast.error('Please set at least one quantity');
				return;
			}

			const { data: res } = await addItems({
				variables: {
					input: {
						orderQuoteId: quoteId,
						items,
					},
				},
			});

			if (res?.addOrderQuoteItems?.success) {
				toast.success(
					`${selected.size} product${selected.size > 1 ? 's' : ''} added to quote`
				);
				onSuccess();
				handleOpenChange(false);
			} else {
				toast.error(res?.addOrderQuoteItems?.message || 'Failed to add products');
			}
		} catch {
			toast.error('Failed to add products to quote');
		}
	};

	const hasValidQuantities = Array.from(selected.values()).some((p) =>
		p.quantities.some((q) => q > 0)
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				className={cn(
					'transition-all duration-300 ease-out',
					step === 'quantities' ? 'sm:max-w-xl' : 'sm:max-w-lg'
				)}
			>
				<DialogHeader>
					<DialogTitle>
						{step === 'products' ? 'Add products' : 'Set quantities'}
					</DialogTitle>
					<DialogDescription>
						{step === 'products'
							? 'Select products to add to this quote.'
							: `Set quantity tiers for ${selected.size} product${selected.size > 1 ? 's' : ''}.`}
					</DialogDescription>
				</DialogHeader>

				{step === 'products' ? (
					<>
						<DialogBody className="flex flex-col gap-0 p-0">
							<div className="relative shrink-0 px-4 pt-4 pb-2">
								<Search
									size={14}
									className="pointer-events-none absolute left-7 top-1/2 -translate-y-1/2 text-text-4"
								/>
								<input
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder="Search products…"
									className="h-8 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-9 pr-3 text-base text-text-1 outline-none placeholder:text-text-4 focus:border-line-3"
									autoFocus
								/>
							</div>

							<div className="scroll-thin h-[400px] overflow-y-auto px-4 py-1">
								<div className="flex flex-col gap-0.5">
									{scroll.items.map((product) => {
										const isSelected = selected.has(product.id);
										const isExisting = existingProductIds.includes(product.id);
										const primaryImage =
											product.images?.find((i) => i.isPrimary) ??
											product.images?.[0];

										return (
											<button
												key={product.id}
												onClick={() => toggleProduct(product)}
												disabled={isExisting}
												title={
													isExisting
														? 'Already on this quote — change its quantities from its row on the quote'
														: undefined
												}
												className={cn(
													'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
													isExisting
														? 'cursor-not-allowed opacity-50'
														: 'cursor-pointer hover:bg-surface-hover',
													isSelected && 'bg-surface-fill'
												)}
											>
												<Checkbox
													checked={isSelected}
													disabled={isExisting}
													tabIndex={-1}
													className="pointer-events-none"
												/>
												{primaryImage?.url ? (
													<ProductImage
														image={primaryImage}
														alt={product.name}
														className="h-10 w-10 shrink-0 rounded-lg border border-line-1"
													/>
												) : (
													<div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line-1 bg-surface-fill">
														<Box size={16} className="text-text-4" />
													</div>
												)}
												<div className="min-w-0 flex-1">
													<div className="truncate text-base font-medium text-text-1">
														{product.name}
													</div>
													<div className="truncate text-xs text-text-4">
														{product.sku}
													</div>
												</div>
												{isExisting && (
													<span className="shrink-0 rounded-md border border-line-2 px-1.5 py-0.5 text-2xs text-text-4">
														Already on this quote
													</span>
												)}
											</button>
										);
									})}
									{scroll.initialLoading && (
										<div className="flex items-center justify-center py-6 text-sm text-text-4">
											Loading…
										</div>
									)}
									{!loading &&
										scroll.items.length === 0 &&
										!scroll.initialLoading && (
											<div className="flex items-center justify-center py-6 text-center text-sm text-text-4">
												{query
													? 'No products match your search.'
													: 'No products available.'}
											</div>
										)}
									{scroll.loadingMore && (
										<div className="py-2 text-center text-xs text-text-4">
											Loading more…
										</div>
									)}
									<div ref={scroll.sentinelRef} className="h-1" />
								</div>
							</div>
						</DialogBody>

						<DialogFooter>
							<Button variant="outline" onClick={() => handleOpenChange(false)}>
								Cancel
							</Button>
							<Button onClick={handleNext} disabled={selected.size === 0}>
								{`Next (${selected.size})`}
							</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogBody>
							<div className="flex flex-col gap-5">
								{Array.from(selected.values()).map((product) => (
									<div key={product.id}>
										<div className="mb-3 flex items-center gap-3">
											<div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line-1 bg-surface-fill">
												{product.imageUrl ? (
													<img
														src={product.imageUrl}
														alt={product.name}
														className="h-full w-full object-cover"
													/>
												) : (
													<Box size={16} className="text-text-4" />
												)}
											</div>
											<div className="min-w-0 flex-1">
												<div className="truncate text-base font-medium text-text-1">
													{product.name}
												</div>
												<div className="text-xs text-text-4">
													{product.sku}
												</div>
											</div>
										</div>

										<div className="flex flex-col gap-1.5 pl-13">
											{product.quantities.map((qty, i) => (
												<div key={i} className="flex items-center gap-1.5">
													<NumberInput
														value={qty || ''}
														onChange={(e) =>
															updateQuantity(
																product.id,
																i,
																parseInt(e.target.value) || 0
															)
														}
														min={1}
														placeholder="Quantity"
														className="flex-1"
													/>
													{product.quantities.length > 1 && (
														<button
															type="button"
															onClick={() =>
																removeQuantity(product.id, i)
															}
															className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
														>
															<X size={14} />
														</button>
													)}
												</div>
											))}
											<button
												type="button"
												onClick={() => addQuantity(product.id)}
												disabled={product.quantities.length >= 3}
												className="flex items-center gap-1 self-start bg-transparent text-sm text-text-3 transition-colors enabled:hover:text-text-1 disabled:cursor-not-allowed disabled:opacity-40"
											>
												<Plus size={12} />
												{product.quantities.length >= 3
													? 'Max 3 quantities'
													: 'Add quantity'}
											</button>
											<div className="mt-2 flex flex-col gap-1">
												<Label>Deadline</Label>
												<DatePicker
													value={
														product.deadline
															? new Date(product.deadline)
															: null
													}
													onChange={(d: Date | undefined) => {
														setSelected((prev) => {
															const next = new Map(prev);
															const entry = next.get(product.id);
															if (!entry) return prev;
															next.set(product.id, {
																...entry,
																deadline: d?.toISOString() ?? null,
															});
															return next;
														});
													}}
													minDate={new Date()}
													placeholder="No deadline"
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						</DialogBody>

						<DialogFooter>
							<Button variant="outline" onClick={() => setStep('products')}>
								Back
							</Button>
							<Button onClick={handleAdd} disabled={adding || !hasValidQuantities}>
								{adding ? (
									<>
										<Loader2 size={14} className="animate-spin" /> Adding…
									</>
								) : (
									'Add products'
								)}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
